import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Modal,
  Input,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography
} from 'antd';
import {
  DownloadOutlined,
  EyeOutlined,
  FilterOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons';
import FilterAdvanceDrawer from '@components/FilterAdvance';
import ContainerBody from '@components/ContainerBody';
import Breadcrumb from '@components/Breadcrumb';
import Page403 from '@page/Common/403/403';
import { usePermission } from '@contexts/Auth/UsePermission';
import { apiGetDeviceInspections } from '@services/ManageDevices/DeviceServices';
import { URL_API } from '@configs/index';
import { convertLocaltimeToUTC, downloadBlobFile, formatCurrency } from '@utils/utils';
import { showError } from '@components/notification';
import './style.scss';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const formatValue = (value, digits = 0) =>
  value === null || value === undefined || value === '' ? '—' : formatCurrency(value, digits);
const formatPercent = (value) =>
  Number(value).toLocaleString('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const formatUtc7 = (value) =>
  value
    ? new Intl.DateTimeFormat('vi-VN', {
        timeZone: 'Asia/Ho_Chi_Minh',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(new Date(value))
    : '—';
export default function DeviceInspection() {
  const { adjustMeterReading } = usePermission();
  const [selectedInspection, setSelectedInspection] = useState(null);
  const [rows, setRows] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [customerKeyword, setCustomerKeyword] = useState('');
  const [serialNo, setSerialNo] = useState('');
  const [status, setStatus] = useState(undefined);
  const [dateRange, setDateRange] = useState(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [filters, setFilters] = useState({});

  const fetchData = useCallback(async () => {
    try {
      setIsFetching(true);
      const res = await apiGetDeviceInspections({ page: currentPage, size: pageSize, ...filters });
      setRows(res.data?.data || []);
      setTotalItems(Number(res.data?.meta?.total || 0));
    } catch (_) {
      showError('Không thể tải kiểm tra sai số thiết bị');
    } finally {
      setIsFetching(false);
    }
  }, [currentPage, pageSize, filters]);
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const makeFilters = () => ({
    ...Object.fromEntries(
      Object.entries(advancedFilters).map(([key, value]) => [
        key,
        Array.isArray(value) ? (value.length ? value : undefined) : value?.trim() || undefined
      ])
    ),
    keyWord: customerKeyword.trim() || undefined,
    serialNo: serialNo.trim() || undefined,
    status,
    fromDate: dateRange?.length ? convertLocaltimeToUTC(dateRange[0], '00:00:00.000') : undefined,
    toDate: dateRange?.length ? convertLocaltimeToUTC(dateRange[1], '23:59:59.999') : undefined
  });
  const handleSearch = () => {
    setCurrentPage(1);
    setFilters(makeFilters());
  };
  const handleReset = () => {
    setAdvancedFilters({});
    setCustomerKeyword('');
    setSerialNo('');
    setStatus(undefined);
    setDateRange(null);
    setCurrentPage(1);
    setFilters({});
  };
  const handleExport = async () => {
    try {
      setExporting(true);
      await downloadBlobFile(
        `${URL_API}/api/device-inspections/export-report`,
        'Bao cao kiem tra sai so thiet bi.xlsx',
        filters,
        'GET'
      );
    } catch (_) {
      showError('Có lỗi khi xuất file Excel');
    } finally {
      setExporting(false);
    }
  };

  const detailColumns = [
    {
      title: 'STT',
      width: 80,
      align: 'center',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1
    },
    { title: 'Thời gian kiểm tra', dataIndex: 'created_at', width: 210, render: formatUtc7 },
    { title: 'Serial', dataIndex: 'serial_no', width: 180 },
    { title: 'IMEI', dataIndex: 'imei', width: 190 },
    { title: 'Model', dataIndex: 'model', width: 210 },
    { title: 'Khách hàng', dataIndex: 'customer_name', width: 280 },
    { title: 'Địa chỉ', dataIndex: 'address', width: 380 },
    {
      title: 'Mốc cài chỉ số (L)',
      dataIndex: 'baseline_meter_reading',
      width: 200,
      render: (value) => formatValue(value)
    },
    {
      title: 'Lít/xung',
      dataIndex: 'litres_per_pulse',
      width: 140,
      render: (value) => formatValue(value)
    },
    {
      title: 'Khóa van',
      dataIndex: 'valve_closed',
      width: 180,
      render: (value) => (
        <Tag color={value === true ? 'success' : value === false ? 'warning' : 'default'}>
          {value === true ? 'Đã khóa' : value === false ? 'Chưa khóa' : 'Chưa xác nhận'}
        </Tag>
      )
    },
    {
      title: 'Chỉ số thực tế (L)',
      dataIndex: 'manual_meter_reading',
      width: 180,
      align: 'right',
      render: (value) => formatValue(value)
    },
    {
      title: 'Chỉ số hệ thống (L)',
      dataIndex: 'device_meter_reading',
      width: 200,
      align: 'right',
      render: (value) => formatValue(value)
    },
    {
      title: 'Sai số tuyệt đối (xung)',
      dataIndex: 'error_pulses',
      width: 160,
      align: 'right',
      render: (value) => formatValue(value)
    },
    {
      title: 'Sai số tương đối (%)',
      dataIndex: 'relative_error_percent',
      width: 160,
      align: 'right',
      render: (v) => (v == null ? '—' : `${formatPercent(v)}%`)
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 160,
      render: (v) => (
        <Tag color={v === 'SUCCESS' ? 'success' : 'error'}>
          {v === 'SUCCESS' ? 'Thành công' : 'Thất bại'}
        </Tag>
      )
    },
    { title: 'Số lần thử', dataIndex: 'attempts', width: 130, align: 'center' },
    {
      title: 'Ảnh',
      dataIndex: 'image_url',
      width: 120,
      render: (v) =>
        v ? (
          <Button
            type="link"
            href={v}
            target="_blank"
            rel="noopener noreferrer"
            style={{ padding: 0, height: 'auto', color: '#0284c7', fontWeight: 600 }}>
            Xem ảnh
          </Button>
        ) : (
          '—'
        )
    },
    { title: 'Kỹ thuật viên', dataIndex: 'created_by', width: 190 },
    { title: 'Ghi chú', dataIndex: 'note', width: 260 }
  ];

  const columns = [
    {
      title: 'STT',
      key: 'index',
      width: 55,
      align: 'center',
      render: (_, __, index) => (currentPage - 1) * pageSize + index + 1
    },
    {
      title: 'ĐBDD - Tên khách hàng',
      key: 'customer',
      width: 190,
      render: (_, row) => (
        <div>
          <Text strong>{row.customer_code || '—'}</Text>
          <div>{row.customer_name || '—'}</div>
        </div>
      )
    },
    {
      title: 'Seri / IMEI',
      key: 'device',
      width: 190,
      render: (_, row) => (
        <div>
          <Text strong>SN: {row.serial_no || '—'}</Text>
          <div className="inspection-secondary">IMEI: {row.imei || '—'}</div>
        </div>
      )
    },
    { title: 'MODEL', dataIndex: 'model', width: 140, render: (value) => value || '—' },
    { title: 'CN', dataIndex: 'branch', width: 160, render: (value) => value || '—' },
    { title: 'TQL', dataIndex: 'manage_group', width: 160, render: (value) => value || '—' },
    { title: 'ĐHK', dataIndex: 'parent_device', width: 140, render: (value) => value || '—' },
    { title: 'Tuyến', dataIndex: 'route', width: 100, render: (value) => value || '—' },
    {
      title: 'Chỉ số khớp lần gần nhất (L)',
      dataIndex: 'baseline_meter_reading',
      width: 170,
      render: (value) => formatValue(value, 3)
    },
    {
      title: (
        <>
          Chỉ số hệ thống
          <br />
          (L)
        </>
      ),
      dataIndex: 'device_meter_reading',
      width: 150,
      render: (value, row) => (
        <div>
          <Text strong>{formatValue(value, 3)}</Text>
          <div className="inspection-secondary">{formatUtc7(row.device_seen_at)}</div>
        </div>
      )
    },
    {
      title: (
        <>
          Chỉ số thực tế
          <br />
          (L)
        </>
      ),
      dataIndex: 'manual_meter_reading',
      width: 150,
      render: (value, row) => (
        <div>
          <Text strong style={{ color: row.status === 'SUCCESS' ? '#16a34a' : undefined }}>
            {formatValue(value, 3)}
          </Text>
          <div className="inspection-secondary">{formatUtc7(row.created_at)}</div>
        </div>
      )
    },
    {
      title: 'Sai số tuyệt đối (Xung)',
      dataIndex: 'error_pulses',
      width: 160,
      align: 'right',
      render: (value) => formatValue(value)
    },
    {
      title: 'Sai số tương đối (%)',
      dataIndex: 'relative_error_percent',
      width: 160,
      align: 'right',
      render: (value) => (value == null ? '—' : `${formatPercent(value)}%`)
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      width: 125,
      render: (value) => (
        <Tag color={value === 'SUCCESS' ? 'success' : 'error'}>
          <span aria-hidden="true">● </span>
          {value === 'SUCCESS' ? 'Thành công' : 'Thất bại'}
        </Tag>
      )
    },
    {
      title: 'Khóa van',
      dataIndex: 'valve_closed',
      width: 140,
      render: (value) => (
        <Tag color={value === true ? 'success' : value === false ? 'warning' : 'default'}>
          {value === true ? 'Đã khóa' : value === false ? 'Chưa khóa' : 'Chưa xác nhận'}
        </Tag>
      )
    },
    {
      title: 'Ngày kiểm tra',
      dataIndex: 'created_at',
      width: 210,
      align: 'center',
      render: formatUtc7
    },
    {
      title: 'Người kiểm tra',
      dataIndex: 'created_by',
      width: 140,
      render: (value) => value || '—'
    },
    {
      title: 'Ảnh',
      dataIndex: 'image_url',
      width: 85,
      align: 'center',
      render: (value) =>
        value ? (
          <Button
            type="link"
            href={value}
            target="_blank"
            rel="noopener noreferrer"
            style={{ padding: 0, height: 'auto', color: '#0284c7', fontWeight: 600 }}>
            Xem ảnh
          </Button>
        ) : (
          '—'
        )
    },
    { title: 'Ghi chú', dataIndex: 'note', width: 180, render: (value) => value || '—' },
    {
      title: 'Thao tác',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (_, row) => (
        <Button
          size="small"
          icon={<EyeOutlined />}
          aria-label="Xem chi tiết kiểm tra"
          title="Xem chi tiết kiểm tra"
          onClick={() => setSelectedInspection(row)}
        />
      )
    }
  ];

  if (!adjustMeterReading.view)
    return (
      <ContainerBody>
        <Page403 />
      </ContainerBody>
    );
  return (
    <ContainerBody>
      <div className="breadcrumb">
        <Breadcrumb
          homePath="/dashboard"
          routeSegments={[{ name: 'Kiểm tra sai số thiết bị', path: '/device/inspection-history' }]}
        />
      </div>
      <Row justify="space-between" align="middle">
        <Col>
          <Title level={4}>Kiểm tra sai số thiết bị</Title>
        </Col>
        <Col>
          {adjustMeterReading.export && (
            <Button
              icon={<DownloadOutlined />}
              loading={exporting}
              onClick={handleExport}
              style={{
                color: '#fff',
                height: 36,
                backgroundColor: '#0284c7',
                borderColor: '#0284c7',
                fontSize: 18
              }}>
              Xuất file excel
            </Button>
          )}
        </Col>
      </Row>
      <Card bodyStyle={{ padding: 8 }}>
        <Row gutter={[8, 8]}>
          <Col md={1} xs={3}>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setIsFilterOpen(true)}
              aria-label="Tìm kiếm nâng cao"
              title="Tìm kiếm nâng cao"
              style={{
                backgroundColor: 'var(--surface-3)',
                color: 'var(--muted)',
                border: 'none',
                height: '36px',
                width: '100%'
              }}
            />
          </Col>
          <Col md={4} xs={21}>
            <Input
              value={customerKeyword}
              onChange={(e) => setCustomerKeyword(e.target.value)}
              onPressEnter={handleSearch}
              placeholder="Tìm kiếm DBĐD, tên khách hàng..."
              style={{ height: 36 }}
            />
          </Col>
          <Col md={5} xs={12}>
            <Input
              value={serialNo}
              onChange={(e) => setSerialNo(e.target.value)}
              onPressEnter={handleSearch}
              placeholder="Tìm kiếm theo serial, IMEI..."
              style={{ height: 36 }}
            />
          </Col>
          <Col md={4} xs={12}>
            <Select
              allowClear
              value={status}
              onChange={setStatus}
              placeholder="Chọn trạng thái"
              style={{ width: '100%', height: 36 }}
              options={[
                { value: 'SUCCESS', label: 'Thành công' },
                { value: 'FAILED', label: 'Thất bại' }
              ]}
            />
          </Col>
          <Col md={5} xs={12}>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              placeholder={['Từ ngày', 'Đến ngày']}
              style={{ width: '100%', height: 36 }}
            />
          </Col>
          <Col md={5} xs={24}>
            <Space>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                style={{
                  height: 36,
                  fontSize: 18,
                  color: '#fff',
                  backgroundColor: '#0284c7',
                  borderColor: '#0284c7'
                }}>
                Tìm kiếm
              </Button>
              <Button icon={<ReloadOutlined />} onClick={handleReset} style={{ height: 36 }}>
                Đặt lại
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>
      <div className="inspection-table-heading">
        <Text strong>Danh sách kết quả kiểm tra ({formatValue(totalItems)})</Text>
        <Space>
          <Text type="secondary">Hiển thị</Text>
          <Select
            value={pageSize}
            options={[10, 20, 50, 100].map((value) => ({ value, label: value }))}
            onChange={(size) => {
              setPageSize(size);
              setCurrentPage(1);
            }}
          />
          <Text type="secondary">bản ghi</Text>
        </Space>
      </div>
      <Table
        className="device-inspection-table"
        size="small"
        bordered
        rowKey="id"
        loading={isFetching}
        dataSource={rows}
        columns={columns}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalItems,
          showSizeChanger: false,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          onShowSizeChange: (_, size) => {
            setCurrentPage(1);
            setPageSize(size);
          },
          showTotal: (total, range) => `${range[0]}-${range[1]} của ${total} bản ghi`
        }}
        tableLayout="fixed"
        style={{ borderRadius: 8 }}
        scroll={{ x: columns.reduce((total, column) => total + column.width, 0), y: 600 }}
      />
      <FilterAdvanceDrawer
        isOpen={isFilterOpen}
        onDrawerClose={() => setIsFilterOpen(false)}
        dataFilterAdvance={advancedFilters}
        setDataFilterAdvance={setAdvancedFilters}
        flagSearch={false}
        setFlagSearch={handleSearch}
        setCurrentPage={setCurrentPage}>
        <Row gutter={[12, 12]}>
          {[
            ['model', 'Model'],
            ['createdBy', 'Người kiểm tra'],
            ['note', 'Ghi chú']
          ].map(([key, label]) => (
            <Col span={24} key={key}>
              <label htmlFor={`inspection-${key}`}>{label}</label>
              <Input
                id={`inspection-${key}`}
                allowClear
                value={advancedFilters[key] || ''}
                placeholder={`Nhập ${label.toLowerCase()}...`}
                onPressEnter={handleSearch}
                onChange={(event) =>
                  setAdvancedFilters((previous) => ({
                    ...previous,
                    [key]: event.target.value
                  }))
                }
              />
            </Col>
          ))}
          <Col span={24}>
            <label htmlFor="inspection-valve">Khóa van</label>
            <Select
              id="inspection-valve"
              allowClear
              placeholder="Tất cả"
              style={{ width: '100%' }}
              value={advancedFilters.valveClosed}
              onChange={(value) =>
                setAdvancedFilters((previous) => ({ ...previous, valveClosed: value }))
              }
              options={[
                { value: 'true', label: 'Đã khóa' },
                { value: 'false', label: 'Chưa khóa' },
                { value: 'unknown', label: 'Chưa xác nhận' }
              ]}
            />
          </Col>
        </Row>
      </FilterAdvanceDrawer>
      <Modal
        title="Chi tiết kết quả kiểm tra"
        open={Boolean(selectedInspection)}
        onCancel={() => setSelectedInspection(null)}
        footer={null}
        width={850}>
        {selectedInspection && (
          <Descriptions bordered column={1} size="small">
            {detailColumns
              .filter((column) => column.dataIndex)
              .map((column) => (
                <Descriptions.Item key={column.dataIndex} label={column.title}>
                  {column.render
                    ? column.render(selectedInspection[column.dataIndex])
                    : selectedInspection[column.dataIndex] ?? '—'}
                </Descriptions.Item>
              ))}
          </Descriptions>
        )}
      </Modal>
    </ContainerBody>
  );
}
